'use client'

import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react'

import styles from './TrackedProductCard.module.scss'

/** Umbral que exige el diccionario para las impresiones (`view_item_list`). */
const IMPRESSION_THRESHOLD = 0.5

interface TrackedProductCardProps {
  /**
   * Posición ABSOLUTA (0-based) del producto en el listado acumulado. No se
   * emite desde aquí (el padre ya la conoce): identifica la card para el
   * observer, que debe reevaluarse si la posición cambiara.
   */
  readonly index: number
  /** Se invoca la primera vez que esta card cruza el 50% del viewport. */
  readonly onImpression: () => void
  /**
   * Se invoca al clicar un enlace real de la card (título o CTA de compra). Recibe
   * el `href` absoluto del enlace clicado (para el `click` de card).
   */
  readonly onSelect: (href: string) => void
  /**
   * Se invoca al clicar el CTA de compra habilitado (`[data-purchase-cta]`) con
   * el destino real (URL absoluta), EN LUGAR de `onSelect` (el CTA es
   * `add_to_cart`, no `select_item`). Ausente ⇒ el CTA cuenta como `onSelect`.
   */
  readonly onPurchase?: (href: string) => void
  /** La card ya renderizada (`VerticalCard`/`HorizontalCard`). */
  readonly children: ReactNode
}

/**
 * `TrackedProductCard` — hoja cliente que instrumenta una card de un listado
 * sin tocar la card en sí (`VerticalCard`/`HorizontalCard`/`RecentlyViewedCard`
 * son Server Components puros, compartidos por 9 módulos: añadirles props de
 * tracking propagaría el cambio a todos). Envuelve a la card ya renderizada,
 * que llega como `children`: eso permite instrumentar un listado desde un
 * Server Component sin arrastrar la card al bundle de cliente.
 *
 * Emite dos señales al padre, que es quien decide si se convierten en evento:
 * - **impresión** (`view_item_list`), vía `IntersectionObserver` con
 *   `threshold: 0.5`.
 * - **click** (`select_item`), por delegación en fase de captura.
 *
 * La deduplicación NO vive aquí: `VirtualizedList` desmonta del DOM las filas
 * fuera de rango, así que este componente (y su observer) se recrean cada vez
 * que el usuario sube y baja. El registro de "ya visto" tiene que sobrevivir a
 * eso, y por eso vive en `useProductListTracking` (`lib/analytics/`).
 */
export function TrackedProductCard({
  index,
  onImpression,
  onSelect,
  onPurchase,
  children,
}: TrackedProductCardProps) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  // El callback se guarda en un ref (actualizado en un efecto, nunca durante el
  // render) para que el observer no se recree cuando el padre lo redefina.
  const onImpressionRef = useRef(onImpression)

  useEffect(() => {
    onImpressionRef.current = onImpression
  })

  useEffect(() => {
    const node = rootRef.current
    if (!node) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        // Basta con la primera vez que cruza el umbral mientras siga montada.
        observer.disconnect()
        onImpressionRef.current()
      },
      { threshold: IMPRESSION_THRESHOLD },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [index])

  /**
   * Fase de CAPTURA: se emite antes de que `next/link` navegue, sin
   * `preventDefault` (la navegación no se retrasa ni se altera). La card expone
   * dos `<a>` hermanos — el título (stretched link, cubre toda la card) y el CTA
   * de compra — y ambos quedan cubiertos por la delegación. Un CTA
   * deshabilitado es un `<button disabled>`, no un `<a href>`, así que
   * `closest` no lo encuentra y no se emite nada.
   */
  function handleClickCapture(event: MouseEvent<HTMLDivElement>) {
    const { target } = event
    if (!(target instanceof Element)) return
    // Enlaces ajenos al producto (p. ej. el enlace editorial de `ContenidoProducto`)
    // se excluyen marcándolos con `data-analytics-ignore`.
    if (target.closest('[data-analytics-ignore]')) return
    const anchor = target.closest<HTMLAnchorElement>('a[href]')
    if (!anchor) return
    const cta = target.closest<HTMLAnchorElement>('a[data-purchase-cta]')
    if (cta && onPurchase) {
      onPurchase(cta.href)
      return
    }
    onSelect(anchor.href)
  }

  return (
    <div ref={rootRef} className={styles.root} onClickCapture={handleClickCapture}>
      {children}
    </div>
  )
}
